/**
 * 在线状态（presence）纯模型（14.38；两线程共用，不触 DOM/Worker API）。
 *
 * 数据源**只有一个**：WS `presence` 事件（protocol §5.3）——payload 是 JSON
 * （非 proto）`{uid, online, platforms}`，服务端只推给好友、**只推变化**，
 * 下线延迟 5s 推（防刷新闪烁）。没有「谁在线」的快照接口：
 * - `friend_group/with_friends`（通讯录册）结构体虽有 Online 字段但服务端漏拷，
 *   恒为 0（protocol §8.9 实锤），不能当在线态用；
 * - 唯一的 HTTP 真值是 `friend/info` 的 `Online`（详情页/聊天标题会拉，14.38b
 *   把它回灌进本 map 当种子），其余地方一律不为补绿点额外发请求（最少请求原则）。
 *
 * 由此而来的**刻意偏差**（下轮别当遗漏补）：
 * - 条目缺失 = `unknown`（不是离线）：对方在我连上之前就在线、服务端不会补推，
 *   所以 UI 必须把 unknown 渲染成「没有标记」而不是「灰点/离线」。
 * - 重连后 map **不清空**（旧版 onlineMap 同口径，只在登出/切号清）：presence
 *   只推变化，清空会让已知在线态永久丢失到对方下次变化为止；代价是断线期间
 *   漏掉的变化会让条目偏旧，由 friend/info 种子在用户真正关心的页面上找平。
 * - 平台列表（ios/android/web/desktop/unknown）原样留着：旧版只用「非空即在线」
 *   判定，新版同口径；留字段是为了日后「在手机上」之类的文案不必改数据链。
 */

import type { ContactsBook } from './contactsModel';
import { formatLastSeenTime } from './formatTime';

/** uid → 当前在线平台列表（空数组 = 离线；无此 key = 未知） */
export type PresenceMap = ReadonlyMap<number, readonly string[]>;

/** 一条 presence 变化（Worker 解析 WS JSON 后的形态） */
export interface PresenceEntry {
  readonly uid: number;
  readonly platforms: readonly string[];
}

export const PRESENCE_EMPTY: PresenceMap = new Map();

export type PresenceState = 'online' | 'offline' | 'unknown';

/** 任意平台在线即「在线」（旧版 `onlineMap[uid]?.isNotEmpty == true` 同口径） */
export function presenceOf(map: PresenceMap, uid: number): PresenceState {
  const platforms = map.get(uid);
  if (platforms === undefined) return 'unknown';
  return platforms.length > 0 ? 'online' : 'offline';
}

function samePlatforms(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * 应用一批变化（帧级合并后的一批，后到的覆盖先到的）。
 * 无净变化返回**原引用**——订阅不白跑（2000 行列表每帧 diff 的前提）。
 */
export function applyPresence(map: PresenceMap, entries: readonly PresenceEntry[]): PresenceMap {
  let next: Map<number, readonly string[]> | null = null;
  for (const entry of entries) {
    if (!Number.isSafeInteger(entry.uid) || entry.uid <= 0) continue; // 畸形帧跳过
    const current = (next ?? map).get(entry.uid);
    if (current !== undefined && samePlatforms(current, entry.platforms)) continue;
    next ??= new Map(map);
    next.set(entry.uid, entry.platforms);
  }
  return next ?? map;
}

/** 私聊会话 key → 对端 uid；群会话/畸形 key 返回 0（presence 与群无关） */
export function peerUidOfKey(key: string): number {
  if (!key.startsWith('dm:')) return 0;
  const uid = Number(key.slice('dm:'.length));
  return Number.isSafeInteger(uid) && uid > 0 ? uid : 0;
}

/**
 * 服务端「禁止查看最近上线」的固定假值（func_friend.go 实锤两处，RFC3339）。
 *
 * ⚠️ 实锤口径（14.38b 复核 go_im，与任务单原注记有出入）：这个假值只写进
 * **LoginTime**，`OfflineTime` 自始至终是 `u.OfflineTime` 原值**不做遮罩**
 * （friend/info 与 with_friends 都是）；friend/list 里那句赋值还被下一行
 * `v.LoginTime = userAvart[...].LoginTime` 当场覆盖，是条死代码。新版只用
 * OfflineTime 做「最近上线」，所以正常走不到这个值——仍然认它一手，免得
 * 哪天服务端把遮罩补到 OfflineTime 上时页面显示「最近上线 2020年10月21日」。
 */
const FAKE_LAST_SEEN_MS = Date.parse('2020-10-21T23:53:32+08:00');

/**
 * RFC3339 离线时间串 → 毫秒时间戳；空串/不可解析/服务端假值一律返回 0
 * （调用方按「没有最近上线时间」处理）。
 */
export function parseLastSeen(raw: string): number {
  const text = raw.trim();
  if (text === '') return 0;
  const ms = Date.parse(text);
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return ms === FAKE_LAST_SEEN_MS ? 0 : ms;
}

/**
 * 在线态文案（私聊标题副标题 / 好友详情页在线行共用）：
 * - online → 「在线」
 * - offline → 有可用离线时间「最近上线 X」，否则「离线」
 * - unknown → **空串**（调用方不渲染该行，不留空行；条目缺失≠离线，文件头口径）
 */
export function presenceStatusText(
  state: PresenceState,
  offlineTimeRaw: string,
  nowMs: number,
): string {
  if (state === 'online') return '在线';
  if (state === 'unknown') return '';
  const at = parseLastSeen(offlineTimeRaw);
  const text = at > 0 ? formatLastSeenTime(at, nowMs) : '';
  return text !== '' ? `最近上线 ${text}` : '离线';
}

/** 从通讯录册反查好友的 OfflineTime（本地已有数据，不为此发请求）；查不到空串 */
export function offlineTimeOfFriend(book: ContactsBook | null, uid: number): string {
  if (book === null || uid <= 0) return '';
  for (const g of book.groups) {
    for (const f of g.friends) {
      if (f.friendId === uid) return f.offlineTime;
    }
  }
  return '';
}
